import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { replace } from "react-router-redux";

import { useMediaQuery } from "@ui-kit/components/index.es";

import { getBoolean } from "util/config";
import * as configUtils from "util/config";
import * as dateUtils from "util/date";
import * as i18n from "util/i18n";
import {
    buildInternalCreditAccountOptions,
    isInternalCreditCvu,
    resolveInternalCreditAccount,
    resolveInternalSendActivityId,
    sanitizeInternalCreditWhenOwnDisabled,
} from "util/internalTransferCreditAccount";
import * as schedulerUtils from "util/scheduler";
import { saveLocation } from "util/transaction";
import { getTransferExecutePath } from "util/v3UxConfig";
import { isUxTransfersImprovementsEnabled, isUxTransfersOwnImprovementsEnabled } from "util/v3ShellLayoutSync";
import { numberFormat } from "util/number";

import { actions as formActions } from "reducers/form";
import { actions as transactionLinesActions } from "reducers/form/transactionLines";
import { actions as holidaysActions } from "reducers/holidays";
import { actions as templateActions } from "reducers/template";
import { actions as transferActions } from "reducers/transfer";

import {
    applyTransferTadHint,
    isTransferExecuteEntryPath,
    matchTransferExecutePath,
    parseTransferAmount,
    resolveTransferAccountRef,
    TRANSFER_INTERNAL_FORM_ID,
    isValidAlias,
    isValidCbu,
} from "v3/pages/transfers/logic/transferExecuteShared";
import { buildTransferInternalValidationSchema } from "v3/pages/transfers/logic/transferExecuteValidation";

const ID_FORM = TRANSFER_INTERNAL_FORM_ID;
const ID_ACTIVITY = "transfers.internal.send";
const ID_ACTIVITY_CVU = "transfersCVU.internal.send";
const ID_ACTIVITY_TAD = "transfers.internal.tad.send";

const INITIAL_VALUES = {
    debitAccount: undefined,
    creditAccount: undefined,
    amount: 0,
    currency: 0,
    sendEmail: false,
    reference: "",
    email: "",
    emailText: "",
    scheduler: {},
};

const FORMIK_INITIAL_VALUES = {
    debitAccount: "",
    creditAccount: "",
    amount: "",
    reference: "",
    sendEmail: false,
    email: "",
    emailText: "",
};

/**
 * Business logic for internal transfer execute (legacy Form + v3 ui-kit page).
 *
 * @param {object} props
 * @returns {object}
 */
export default function useTransferInternalExecuteLogic(props) {
    const {
        dispatch,
        isDesktop,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        location,
        accounts,
        externalOwnAccounts = [],
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        summary,
        limit,
        limitUsed,
        onCreateTemplate,
        agendaVisible,
        setAgendaVisible,
        isCVU,
        lang,
        hideAmount,
        limits,
        idTransaction,
        activeEnvironment,
        isRetailEnvironment,
    } = props;

    const [values, setValues] = useState(INITIAL_VALUES);
    const [draftButtonDisabled, setDraftButtonDisabled] = useState(false);
    const [formikProps, setFormikProps] = useState({});
    const [tadWarningPopupVisible, setTadWarningPopupVisible] = useState(false);
    const [tadWarningShowed, setTadWarningShowed] = useState(false);
    const tadWarningAcknowledgedRef = useRef(false);
    const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
    const isMobile = useMediaQuery("min", "md");

    const { debitAccount, creditAccount, amount, reference, email, emailText, sendEmail } = values;

    const debitAccountRef = useRef(debitAccount);
    useEffect(() => {
        debitAccountRef.current = debitAccount;
    }, [debitAccount]);

    const extendedAmountVariableEnabled = getBoolean("increase.size.amount.link.iso8583", false);
    const enableHideAmounts = getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const referenceMaxLength = configUtils.getInteger("frontend.transfers.reference.maxLength", 12);
    const tadWarningText = configUtils.get("frontend.TAD.warning.text");

    const getActivityIds = useCallback(() => {
        const cvuCoelsaEnabled = configUtils.getBoolean(
            "frontend.show.transfers.services.Coelsa.CVU.functionalities",
            false,
        );
        if (isUxTransfersOwnImprovementsEnabled() && cvuCoelsaEnabled) {
            return [ID_ACTIVITY, ID_ACTIVITY_CVU];
        }
        return [ID_ACTIVITY];
    }, []);

    const getActivityId = useCallback(() => {
        const isEnableCVUCoelsaTransfer = configUtils.getBoolean(
            "frontend.show.transfers.services.Coelsa.CVU.functionalities",
        );
        const ownUxEnabled = isUxTransfersOwnImprovementsEnabled();
        const resolvedCredit = resolveInternalCreditAccount(creditAccount, accounts, externalOwnAccounts, ownUxEnabled);
        return resolveInternalSendActivityId({
            ownUxEnabled,
            cvuCoelsaEnabled: isEnableCVUCoelsaTransfer,
            reduxIsCvu: isCVU,
            creditAccount: resolvedCredit || creditAccount,
        });
    }, [accounts, creditAccount, externalOwnAccounts, isCVU]);

    const getActivityIdTad = useCallback(() => ID_ACTIVITY_TAD, []);

    const parseAmount = parseTransferAmount;

    const isTad = useCallback(
        (amountWithCurrency) => {
            const value = amountWithCurrency.amount;
            const environmentTypeTag = isRetailEnvironment ? "Retail" : "Corporate";
            const currencyTag = amountWithCurrency.currency.toString() === "0" ? "Pesos" : "Dolares";
            const minAmount = configUtils.getInteger(
                `frontend.TAD.minAmount${currencyTag}.${environmentTypeTag}`,
                100000,
            );
            const maxAmount = configUtils.getInteger(
                `frontend.TAD.maxAmount${currencyTag}.${environmentTypeTag}`,
                1000000,
            );

            if (isCVU) {
                return false;
            }

            if (activeEnvironment?.permissions?.transferDiferredAccreditation) {
                if (value >= minAmount && value <= maxAmount) {
                    return true;
                }
            }
            return false;
        },
        [activeEnvironment, isCVU, isRetailEnvironment],
    );

    const setHint = useCallback(
        (value) => {
            applyTransferTadHint(isTad(value), {
                setPopupVisible: setTadWarningPopupVisible,
                setShowed: setTadWarningShowed,
                isAcknowledged: () => tadWarningAcknowledgedRef.current,
                resetAcknowledged: () => {
                    tadWarningAcknowledgedRef.current = false;
                },
                onActive: () => {
                    if (agendaVisible) {
                        setAgendaVisible(false);
                    }
                },
                onInactive: () => {
                    setAgendaVisible(true);
                },
            });
        },
        [agendaVisible, isTad, setAgendaVisible],
    );

    const selectedAccount = useCallback(() => accounts.find((account) => account.idProduct === values.debitAccount), [
        accounts,
        values.debitAccount,
    ]);

    const getDefaultAccount = useCallback(() => {
        const summaryDebit = summary?.debitAccount;
        if (summaryDebit) {
            return summaryDebit.value || summaryDebit.idProduct || summaryDebit;
        }
        return accounts?.find((account) => account.favorite)?.idProduct || accounts[0]?.idProduct;
    }, [accounts, summary?.debitAccount]);

    const selectedCurrency = useCallback(() => {
        const selectedAccountForLabel = accounts.find((account) => account.idProduct === values.debitAccount);
        return selectedAccountForLabel?.currency ? i18n.get(`currency.label.${selectedAccountForLabel.currency}`) : "";
    }, [accounts, values.debitAccount]);

    const mapAccounts = useMemo(
        () =>
            accounts.map((account) => ({
                accountName: account.productAlias || account.shortLabel,
                amount: account.balance,
                currency: account.currency ? i18n.get(`currency.label.${account.currency}`) : "",
                description: account.productType ? i18n.get(`productType.${account.productType}`) : "",
                id: account.idProduct,
                codeBank: account.cbu?.substring(0, 3) || "319",
            })),
        [accounts],
    );

    const debitAccountCbu = useMemo(() => {
        const debit = accounts.find((account) => account.idProduct === values.debitAccount);
        return debit?.cbu;
    }, [accounts, values.debitAccount]);

    const creditAccountOptions = useMemo(
        () =>
            buildInternalCreditAccountOptions({
                cmfOptions: mapAccounts,
                externalOwnAccounts,
                debitAccountId: values.debitAccount,
                debitCurrencyLabel: selectedCurrency(),
                debitCbu: debitAccountCbu,
                getCurrencyLabel: (code) => i18n.get(`currency.label.${code}`),
                ownUxEnabled: isUxTransfersOwnImprovementsEnabled(),
            }),
        [debitAccountCbu, externalOwnAccounts, mapAccounts, selectedCurrency, values.debitAccount],
    );

    const validationSchema = useMemo(() => buildTransferInternalValidationSchema(i18n.get, sendEmail), [sendEmail]);

    const isDraftButtonInactive = useMemo(
        () => (draftButtonDisabled && !matchTransferExecutePath(location?.pathname, "internal")) || fetchingDraft,
        [draftButtonDisabled, fetchingDraft, location?.pathname],
    );

    const showInitialSpinner = useMemo(() => (fetching && !accounts.length) || !values.debitAccount || fetchingSubmit, [
        accounts.length,
        fetching,
        fetchingSubmit,
        values.debitAccount,
    ]);

    useEffect(() => {
        const isDraftDisabled = !values.debitAccount || !values.creditAccount || !values.amount;
        setDraftButtonDisabled(isDraftDisabled);
    }, [values.debitAccount, values.creditAccount, values.amount]);

    useEffect(() => {
        const { debitAccount: debitAccountDraft } = values;
        dispatch(transferActions.preActivity());
        dispatch(holidaysActions.listHolidays());
        const { cbu, email: summaryEmail, transferKind, isFrequentDestinations } = summary;

        if (isTransferExecuteEntryPath(location?.pathname)) {
            saveLocation(props);
            if (location?.state) {
                const { isInternal, debitAccount: stateDebitAccount } = location.state;
                dispatch(transferActions.changeTransferGroup(isInternal));
                setValues((prev) => ({
                    ...prev,
                    debitAccount: stateDebitAccount,
                    creditAccount: "",
                    amount: {
                        amount: 0,
                        currency: "",
                    },
                    cbuOrAlias: "",
                    reference: "",
                    email: "",
                    emailText: "",
                    concept: "VAR",
                    transferKind: "",
                }));
                dispatch(transferActions.accountLimitsListActivities(stateDebitAccount, getActivityIds()));
            } else {
                const isInternalTrx = transferKind === "other" ? false : activeEnvironment.permissions.transferInternal;

                if (!isInternalTrx && isFrequentDestinations) {
                    if (cbu) {
                        setValues((prev) => ({
                            ...prev,
                            cbuOrAlias: cbu,
                            email: summaryEmail,
                            transferKind,
                        }));
                    }
                } else if (summaryEmail && isFrequentDestinations) {
                    setValues((prev) => ({
                        ...prev,
                        email: summaryEmail,
                    }));
                }
                dispatch(transferActions.changeTransferGroup(isInternalTrx));
            }
        } else if (matchTransferExecutePath(location?.pathname, "internal", "modify") && debitAccountDraft) {
            dispatch(transferActions.accountLimitsListActivities(debitAccountDraft, getActivityIds()));
        }
    }, []);

    useEffect(() => {
        dispatch(templateActions.loadTemplateList(ID_FORM));
    }, [dispatch]);

    useEffect(() => {
        const {
            debitAccount: summaryDebitAccount,
            creditAccount: summaryCreditAccount,
            reference: summaryReference,
            amount: summaryAmount,
            email: summaryEmail,
            emailText: summaryEmailText,
            sendEmail: summarySendEmail,
            scheduler,
        } = summary;
        const resolvedDebit = resolveTransferAccountRef(summaryDebitAccount);
        const resolvedCredit = sanitizeInternalCreditWhenOwnDisabled(
            resolveTransferAccountRef(summaryCreditAccount),
            isUxTransfersOwnImprovementsEnabled(),
        );

        if (resolvedDebit && resolvedDebit !== debitAccountRef.current) {
            dispatch(transferActions.accountLimitsListActivities(resolvedDebit, getActivityIds()));
        }

        setValues((prev) => ({
            ...prev,
            ...(resolvedDebit ? { debitAccount: resolvedDebit } : {}),
            creditAccount: resolvedCredit || "",
            reference: summaryReference || "",
            amount: summaryAmount || "",
            email: summaryEmail || "",
            emailText: summaryEmailText || "",
            sendEmail: summarySendEmail,
            scheduler,
        }));
    }, [summary, dispatch, getActivityIds]);

    useEffect(() => {
        if (fetchingSubmit || !amount || !debitAccount) {
            return;
        }
        const parsedAmountValue = parseAmount(amount);
        const foundAccount = accounts?.find((account) => account.idProduct === debitAccount);
        if (foundAccount) {
            const { currency } = foundAccount;
            if (parsedAmountValue > 0) {
                setHint({ amount: parsedAmountValue, currency });
            }
        }
    }, [accounts, amount, debitAccount, fetchingSubmit, parseAmount, setHint]);

    useEffect(() => {
        if (creditAccount && creditAccount === debitAccount) {
            setValues((prev) => ({
                ...prev,
                creditAccount: undefined,
            }));
            return;
        }
        const debit = accounts.find((account) => account.idProduct === debitAccount);
        if (creditAccount && debit?.cbu && creditAccount === debit.cbu) {
            setValues((prev) => ({
                ...prev,
                creditAccount: undefined,
            }));
        }
    }, [accounts, creditAccount, debitAccount]);

    useEffect(() => {
        if (!selectedAccount()) {
            const defaultAccount = getDefaultAccount();
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
                if (formikProps?.setValues) {
                    formikProps.setValues({ debitAccount: defaultAccount });
                }
            }
        }
    }, [values.debitAccount]);

    useEffect(() => {
        if (!values.debitAccount) {
            const defaultAccount = getDefaultAccount();
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
                if (formikProps?.setValues) {
                    formikProps.setValues({ debitAccount: defaultAccount });
                }
            }
        }
    }, [values.debitAccount, accounts, summary?.debitAccount]);

    useEffect(() => {
        if (formikProps?.setValues) {
            formikProps.setValues({
                debitAccount,
                creditAccount,
                amount,
                reference,
                sendEmail,
                email,
                emailText,
            });
        }
    }, [amount, creditAccount, debitAccount, email, emailText, formikProps, reference, sendEmail]);

    const verifyCvu = useCallback(
        (value) => {
            if (isValidCbu(value)) {
                const isACVU = value.startsWith("000");
                dispatch(transferActions.setIsCVU(isACVU));
            } else if (isValidAlias(value)) {
                dispatch(transferActions.getCbuCvuFromAlias(value));
            }
        },
        [dispatch],
    );

    const changeCbu = useCallback(
        (record) => {
            const transferKindDestination = record.isOwn === "1" ? "same" : "other";
            setValues((prev) => ({
                ...prev,
                cbuOrAlias: record.account,
                transferKind: transferKindDestination,
                email: record.email,
            }));
            verifyCvu(record.account);
        },
        [verifyCvu],
    );

    const closeTADModal = useCallback(() => {
        tadWarningAcknowledgedRef.current = true;
        setTadWarningPopupVisible(false);
    }, []);

    const openTADModal = useCallback(() => {
        setTadWarningPopupVisible(true);
    }, []);

    const handleDebitAccountSelect = useCallback(
        (accountId) => {
            setValues((prev) => ({ ...prev, debitAccount: accountId }));
            dispatch(transferActions.accountLimitsListActivities(accountId, getActivityIds()));
        },
        [dispatch],
    );

    const handleCreditAccountSelect = useCallback(
        (accountId) => {
            setValues((prev) => ({ ...prev, creditAccount: accountId }));
            const resolvedCredit = resolveInternalCreditAccount(
                accountId,
                accounts,
                externalOwnAccounts,
                isUxTransfersOwnImprovementsEnabled(),
            );
            dispatch(transferActions.setIsCVU(isInternalCreditCvu(resolvedCredit || accountId)));
        },
        [accounts, dispatch, externalOwnAccounts],
    );

    const handleCreateTemplate = useCallback(
        (propsFormik) => {
            const account = accounts.find((accountItem) => accountItem.idProduct === values.debitAccount);
            const { currency } = account;
            onCreateTemplate({ ...propsFormik.values, currency });
        },
        [accounts, onCreateTemplate, values.debitAccount],
    );

    const handleChangeAmount = useCallback((nextAmount) => {
        setValues((prev) => ({ ...prev, amount: nextAmount }));
    }, []);

    const handleReferenceChange = useCallback((nextReference) => {
        setValues((prev) => ({ ...prev, reference: nextReference }));
    }, []);

    const handleSendEmailToggle = useCallback(() => {
        setValues((prev) => ({ ...prev, sendEmail: !prev.sendEmail }));
    }, []);

    const handleEmailChange = useCallback((nextEmail) => {
        setValues((prev) => ({ ...prev, email: nextEmail }));
    }, []);

    const handleEmailTextChange = useCallback((nextEmailText) => {
        setValues((prev) => ({ ...prev, emailText: nextEmailText }));
    }, []);

    const handleTemplateSelect = useCallback(
        (template) => {
            const templateAmount = template.amount?.amount || 0;
            const amountWithCurrency = template.amount;
            const isTadTrx = isTad(amountWithCurrency);
            const ownUxEnabled = isUxTransfersOwnImprovementsEnabled();
            const safeCredit = sanitizeInternalCreditWhenOwnDisabled(template.creditAccount, ownUxEnabled);
            setIsTemplatesVisible(false);
            setValues((prev) => ({
                ...prev,
                debitAccount: template.debitAccount,
                creditAccount: safeCredit || "",
                transferKind: template.transferKind,
                cbuOrAlias: template.cbuOrAlias,
                amount: templateAmount,
                concept: template.concept,
                reference: template.reference,
                sendEmail: template.sendEmail,
                emailText: template.emailText,
                email: template.email,
                flagHint: isTadTrx,
                tadTrx: isTadTrx,
            }));

            dispatch(transactionLinesActions.loadTransactionLinesTemplateData(template));
            if (template.debitAccount) {
                dispatch(transferActions.accountLimitsListActivities(template.debitAccount, getActivityIds()));
            }
        },
        [dispatch, getActivityIds, isTad, setIsTemplatesVisible],
    );

    const getDraftSummary = useCallback(() => {
        const {
            debitAccount: draftDebitAccount,
            creditAccount: draftCreditAccount,
            amount: draftAmount,
            reference: draftReference,
            sendEmail: draftSendEmail,
            email: draftEmail,
            emailText: draftEmailText,
            tadTrx,
            scheduler,
            flagHint,
        } = values;

        const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === draftDebitAccount);
        const schedulerToSend = scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? scheduler : null;
        const completeCreditAccount = resolveInternalCreditAccount(
            draftCreditAccount,
            accounts,
            externalOwnAccounts,
            isUxTransfersOwnImprovementsEnabled(),
        );

        let parsedAmount;
        let currency;
        if (draftAmount && typeof draftAmount === "object") {
            ({ amount: parsedAmount, currency } = draftAmount);
        } else {
            parsedAmount = parseAmount(draftAmount);
            currency = completeDebitAccount?.currency;
        }

        return {
            debitAccount: completeDebitAccount,
            creditAccount: completeCreditAccount,
            currency,
            amount: parsedAmount,
            reference: draftReference,
            sendEmail: draftSendEmail,
            email: draftEmail,
            emailText: draftEmailText,
            scheduler: schedulerToSend,
            idTransaction,
            tadTrx,
            flagHint,
        };
    }, [accounts, externalOwnAccounts, idTransaction, parseAmount, values]);

    const onSaveDraft = useCallback(
        (draftValues) => {
            const { transferKind, concept, debitAccount: draftDebitAccount } = draftValues;

            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === draftDebitAccount);
            const parsedAmount = parseAmount(draftValues.amount) || 0;
            const tadTrx = isTad({
                currency: completeDebitAccount.currency,
                amount: parsedAmount,
            });
            dispatch(
                formActions.saveDraft({
                    idForm: ID_FORM,
                    idActivityDraft: tadTrx ? getActivityIdTad() : getActivityId(),
                    data: {
                        ...draftValues,
                        amount: {
                            quantity: parsedAmount,
                            currency: completeDebitAccount.currency,
                        },
                        debitAccount: { value: draftDebitAccount },
                        creditAccount: { value: draftValues.creditAccount },
                        transferKind: transferKind?.value,
                        concept: concept?.value,
                    },
                    idTransaction: idTransaction || null,
                    scheduler: draftValues?.scheduler,
                    summary: getDraftSummary(),
                }),
            );
        },
        [accounts, dispatch, getActivityId, getActivityIdTad, getDraftSummary, idTransaction, isTad, parseAmount],
    );

    const onSubmit = useCallback(
        ({
            debitAccount: submitDebitAccount,
            creditAccount: submitCreditAccount,
            amount: submitAmount,
            reference: submitReference,
            sendEmail: submitSendEmail,
            email: submitEmail,
            emailText: submitEmailText,
            flagHint,
        }) => {
            if (tadWarningPopupVisible) {
                return;
            }
            const { scheduler } = values;

            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === submitDebitAccount);
            const completeCreditAccount = resolveInternalCreditAccount(
                submitCreditAccount,
                accounts,
                externalOwnAccounts,
                isUxTransfersOwnImprovementsEnabled(),
            );
            const currency = parseInt(completeDebitAccount.currency, 10);

            let schedulerToSend =
                scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? { ...scheduler } : null;
            if (schedulerToSend) {
                schedulerToSend = { ...schedulerToSend, newValueDate: schedulerToSend.valueDate };
                delete schedulerToSend.valueDate;
            }

            const tadTrx = isTad({
                currency,
                amount: parseAmount(submitAmount),
            });

            if (isDesktop) {
                dispatch(replace(getTransferExecutePath("internal", { suffix: "back" })));
            }
            dispatch(
                transferActions.createInternalTransfer(
                    {
                        debitAccount: completeDebitAccount,
                        creditAccount: completeCreditAccount,
                        currency,
                        amount: parseAmount(submitAmount),
                        reference: submitReference,
                        sendEmail: submitSendEmail,
                        email: submitEmail,
                        emailText: submitEmailText,
                        scheduler: schedulerToSend,
                        idTransaction: summary.idTransaction,
                        tadTrx,
                        flagHint,
                    },
                    null,
                ),
            );
        },
        [
            accounts,
            dispatch,
            externalOwnAccounts,
            isDesktop,
            isTad,
            parseAmount,
            summary.idTransaction,
            tadWarningPopupVisible,
            values,
        ],
    );

    const handleSchedulerChange = useCallback((data) => {
        if (data.frequency === schedulerUtils.ONCE) {
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: data.frequency,
                    valueDate: data.startsOn,
                    program: null,
                },
            }));
        } else {
            const newProgramData = {
                frequency: data.frequency === schedulerUtils.CUSTOM ? data.customFreq : data.frequency,
                frequencyValue: data.frequency === schedulerUtils.CUSTOM ? data.customFreqValue : "1",
                days: dateUtils.getDayFromDate(data.startsOn),
                day: data.startsOn.getDate(),
                occurrence: null,
                startsOn: data.startsOn,
                lapse: data.lapse.lapse,
                number: data.lapse.number,
                date: data.lapse.date,
                isCustom: data.frequency === schedulerUtils.CUSTOM,
                selectedOption: data.frequency,
            };
            if (newProgramData.frequency === schedulerUtils.WEEK && newProgramData.isCustom) {
                newProgramData.days = data.days;
            }
            if (newProgramData.frequency === schedulerUtils.MONTH && data.occurrenceType === "occurrence") {
                newProgramData.occurrence = dateUtils.weekOfMonth(moment(data.startsOn));
                newProgramData.day = null;
            }
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: newProgramData.frequency,
                    program: newProgramData,
                },
            }));
        }
        setIsScheduleModalVisible(false);
    }, []);

    const openScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(true);
    }, []);

    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(false);
    }, []);

    const customHandleKeyDown = useCallback(
        (e) => {
            const { decimalSeparator } = numberFormat(lang);
            const { value, selectionStart, selectionEnd } = e.target;
            const integerPart = value.split(decimalSeparator)[0].replace(/\D/g, "");
            const extendedAmountEnabled = getBoolean("increase.size.amount.link.iso8583", false);
            const maxSize = extendedAmountEnabled ? 15 : 10;

            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Home",
                "End",
                "Control",
                "Meta",
                "Shift",
            ];

            if (e.ctrlKey || e.metaKey || e.shiftKey) {
                return;
            }

            const isAllSelected = selectionStart === 0 && selectionEnd === value.length;
            const decimalIndex = value.indexOf(decimalSeparator);

            if (
                (e.key === decimalSeparator || e.key === ".") &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                integerPart.length >= maxSize
            ) {
                return;
            }

            if (isAllSelected) {
                return;
            }

            if (
                integerPart.length >= maxSize &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                !allowedKeys.includes(e.key)
            ) {
                e.preventDefault();
            }
        },
        [lang],
    );

    const handleSaveDraftClick = useCallback(
        (propsFormik) => {
            if (!isUxTransfersImprovementsEnabled()) {
                setDraftButtonDisabled(true);
            }
            onSaveDraft(propsFormik.values);
        },
        [onSaveDraft],
    );

    const handleAmountBlur = useCallback(
        (nextAmount) => {
            handleChangeAmount(nextAmount);
        },
        [handleChangeAmount],
    );

    return {
        idForm: ID_FORM,
        idActivity: getActivityId(),
        values,
        setValues,
        debitAccount,
        creditAccount,
        amount,
        reference,
        email,
        emailText,
        sendEmail,
        scheduler: values.scheduler,
        draftButtonDisabled,
        isDraftButtonInactive,
        formikProps,
        setFormikProps,
        initialValues: FORMIK_INITIAL_VALUES,
        validationSchema,
        tadWarningPopupVisible,
        setTadWarningPopupVisible,
        tadWarningShowed,
        setTadWarningShowed,
        isScheduleModalVisible,
        setIsScheduleModalVisible,
        isMobile,
        isDesktop,
        extendedAmountVariableEnabled,
        enableHideAmounts,
        referenceMaxLength,
        tadWarningText,
        hideAmount,
        limit,
        limits,
        limitUsed,
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        agendaVisible,
        summary,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        showInitialSpinner,
        mapAccounts,
        creditAccountOptions,
        selectedAccount,
        selectedCurrency,
        parseAmount,
        isTad,
        isValidAlias,
        isValidCbu,
        verifyCvu,
        changeCbu,
        closeTADModal,
        openTADModal,
        getActivityId,
        getActivityIds,
        getActivityIdTad,
        handleDebitAccountSelect,
        handleCreditAccountSelect,
        handleCreateTemplate,
        handleChangeAmount,
        handleReferenceChange,
        handleSendEmailToggle,
        handleEmailChange,
        handleEmailTextChange,
        handleTemplateSelect,
        getDraftSummary,
        onSaveDraft,
        onSubmit,
        handleSchedulerChange,
        openScheduleModal,
        closeScheduleModal,
        customHandleKeyDown,
        handleSaveDraftClick,
        handleAmountBlur,
    };
}
